import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  TextField,
  Typography
} from "@material-ui/core";

const COLORS = {
  background: "#f7f8fa",
  card: "#ffffff",
  text: "#111827",
  muted: "#667085",
  border: "#e5e7eb",
  primary: "#111827",
  soft: "#f3f4f6"
};

const optionsStyle = {
  width: "100%",
  textTransform: "none",
  justifyContent: "flex-start",
  textAlign: "left",
  borderRadius: 14,
  padding: "14px 16px",
  marginBottom: 10,
  borderColor: COLORS.border,
  color: COLORS.text,
  background: "#fff",
  fontSize: 15
};

const steps = [
  "start",
  "name",
  "business",
  "segment",

  "presenceSearch",
  "presenceStructure",

  "authorityProof",
  "authorityPresentation",

  "conversionProcess",
  "conversionFollowup",

  "operationOrganization",
  "operationManual",

  "goal",
  "phone",
  "processing",
  "result"
];

const scoreLabel = score => {
  if (score >= 80) return "Muito bem estruturado";
  if (score >= 60) return "Boa base, com oportunidades";
  if (score >= 40) return "Precisa de atenção";
  return "Gargalo importante";
};

const resultContent = {
  presence: {
    name: "Presença",
    title: "Seu principal gargalo está em Presença Digital",
    description:
      "Sua empresa pode estar deixando oportunidades na mesa antes mesmo do primeiro contato. O próximo passo é fortalecer como o negócio é encontrado no Google e nos principais pontos digitais.",
    priorities: [
      "Melhorar sua presença nas buscas",
      "Organizar Google, informações e canais",
      "Criar uma estrutura que facilite ser encontrado"
    ]
  },
  authority: {
    name: "Autoridade",
    title: "Seu principal gargalo está em Autoridade Digital",
    description:
      "Ser encontrado é apenas o começo. Sua estrutura precisa transmitir confiança rapidamente para que o potencial cliente perceba valor e escolha sua empresa.",
    priorities: [
      "Fortalecer posicionamento e comunicação",
      "Aumentar provas e avaliações",
      "Melhorar a percepção digital da empresa"
    ]
  },
  conversion: {
    name: "Conversão",
    title: "Seu principal gargalo está em Conversão",
    description:
      "Sua empresa já pode estar recebendo oportunidades, mas parte delas provavelmente se perde entre o primeiro contato, o atendimento e o fechamento.",
    priorities: [
      "Organizar o atendimento pelo WhatsApp",
      "Criar um processo claro de acompanhamento",
      "Melhorar velocidade, oferta e follow-up"
    ]
  },
  operation: {
    name: "Operação",
    title: "Seu principal gargalo está na Operação Digital",
    description:
      "Seu atendimento ainda depende bastante de esforço manual. Processos, agenda e automação podem liberar tempo e impedir que oportunidades sejam perdidas.",
    priorities: [
      "Organizar atendimento e agenda",
      "Reduzir tarefas repetitivas",
      "Usar CRM, automação e IA onde fizer sentido"
    ]
  }
};


// ============================================================
// DIAGNOSTIC REPORT INTELLIGENCE V2
// ============================================================

const pillarVisual = {
  presence: {
    name: "Presença Digital",
    short: "Presença",
    color: "#22C55E",
    soft: "#F0FDF4"
  },
  authority: {
    name: "Autoridade Digital",
    short: "Autoridade",
    color: "#8B5CF6",
    soft: "#F5F3FF"
  },
  conversion: {
    name: "Conversão Digital",
    short: "Conversão",
    color: "#F59E0B",
    soft: "#FFFBEB"
  },
  operation: {
    name: "Operação Digital",
    short: "Operação",
    color: "#0EA5E9",
    soft: "#F0F9FF"
  }
};

const getMaturity = score => {
  if (score >= 90) {
    return {
      label: "Estrutura avançada",
      color: "#16A34A",
      soft: "#F0FDF4"
    };
  }

  if (score >= 75) {
    return {
      label: "Estrutura consolidada",
      color: "#2563EB",
      soft: "#EFF6FF"
    };
  }

  if (score >= 60) {
    return {
      label: "Estrutura em desenvolvimento",
      color: "#D97706",
      soft: "#FFFBEB"
    };
  }

  if (score >= 40) {
    return {
      label: "Estrutura em construção",
      color: "#EA580C",
      soft: "#FFF7ED"
    };
  }

  return {
    label: "Estrutura frágil",
    color: "#DC2626",
    soft: "#FEF2F2"
  };
};

const buildPriorities = form => {
  const priorities = [];

  const add = (id, title, description) => {
    if (
      priorities.length < 3 &&
      !priorities.some(item => item.id === id)
    ) {
      priorities.push({
        id,
        title,
        description
      });
    }
  };

  if (
    form.operationOrganization !== null &&
    form.operationOrganization <= 1
  ) {
    add(
      "crm",
      "Centralizar contatos e oportunidades",
      "Organize os contatos em um CRM para saber quem chegou, em qual etapa está e qual é o próximo passo."
    );
  }

  if (
    form.conversionFollowup !== null &&
    form.conversionFollowup <= 1
  ) {
    add(
      "followup",
      "Criar um processo de acompanhamento",
      "Defina retornos para quem demonstrou interesse e ainda não tomou uma decisão."
    );
  }

  if (
    form.operationManual !== null &&
    form.operationManual <= 1
  ) {
    add(
      "automation",
      "Reduzir a dependência de tarefas manuais",
      "Automatize etapas repetitivas do atendimento, agendamento e acompanhamento sem perder o controle humano."
    );
  }

  if (
    form.conversionProcess !== null &&
    form.conversionProcess <= 1
  ) {
    add(
      "sales-process",
      "Estruturar o caminho do primeiro contato até a decisão",
      "Padronize resposta, qualificação e condução para que oportunidades não se percam durante o atendimento."
    );
  }

  if (
    form.presenceSearch !== null &&
    form.presenceSearch <= 1
  ) {
    add(
      "google",
      "Melhorar sua capacidade de ser encontrado",
      "Fortaleça Google, presença local e páginas que respondam às buscas que seus potenciais clientes já fazem."
    );
  }

  if (
    form.presenceStructure !== null &&
    form.presenceStructure <= 1
  ) {
    add(
      "digital-structure",
      "Construir uma base digital mais profissional",
      "Organize site, Google, canais de contato e informações da empresa para não depender somente das redes sociais."
    );
  }

  if (
    form.authorityProof !== null &&
    form.authorityProof <= 1
  ) {
    add(
      "proof",
      "Aumentar suas provas de confiança",
      "Colete e apresente avaliações, depoimentos e outros sinais que reduzam a insegurança antes da compra."
    );
  }

  if (
    form.authorityPresentation !== null &&
    form.authorityPresentation <= 1
  ) {
    add(
      "presentation",
      "Elevar a percepção profissional da empresa",
      "Faça sua presença digital representar melhor a qualidade que você já entrega no mundo real."
    );
  }

  // Se os gargalos não forem extremos, complementamos conforme objetivo.
  if (form.goal === "Ser encontrado por mais clientes") {
    add(
      "goal-acquisition",
      "Expandir sua presença nas buscas",
      "Transforme Google, conteúdo e ativos digitais em canais permanentes de aquisição."
    );
  }

  if (form.goal === "Melhorar presença e autoridade") {
    add(
      "goal-authority",
      "Fortalecer posicionamento e confiança",
      "Organize sua apresentação, diferenciais e provas para aumentar a percepção de valor."
    );
  }

  if (
    form.goal ===
    "Converter melhor os contatos que já chegam"
  ) {
    add(
      "goal-conversion",
      "Melhorar conversão dos contatos atuais",
      "Estruture atendimento, acompanhamento e condução comercial antes de buscar mais volume."
    );
  }

  if (
    form.goal ===
    "Automatizar atendimento e agendamento"
  ) {
    add(
      "goal-automation",
      "Automatizar atendimento e agendamento",
      "Reduza o tempo gasto com tarefas repetitivas e mantenha oportunidades sendo atendidas mesmo quando a equipe estiver ocupada."
    );
  }

  if (
    form.goal ===
    "Estruturar tudo isso de forma profissional"
  ) {
    add(
      "goal-full",
      "Criar uma estrutura digital integrada",
      "Conecte presença, autoridade, atendimento, CRM e operação em uma única estratégia."
    );
  }

  // Fallbacks para garantir três prioridades úteis.
  add(
    "measurement",
    "Acompanhar os indicadores certos",
    "Meça origem dos contatos, conversão, tempo de resposta e oportunidades para saber onde agir primeiro."
  );

  add(
    "journey",
    "Organizar a jornada do cliente",
    "Garanta continuidade entre ser encontrado, entrar em contato, receber atendimento e avançar até uma decisão."
  );

  add(
    "review",
    "Revisar a estrutura periodicamente",
    "Use os quatro pilares como referência para decidir onde concentrar investimento e esforço."
  );

  return priorities.slice(0, 3);
};

const buildExecutiveReading = (
  scores,
  strongestKey,
  primaryGap
) => {
  const {
    presence,
    authority,
    conversion,
    operation
  } = scores;

  if (
    presence >= 70 &&
    authority >= 70 &&
    (conversion < 60 || operation < 60)
  ) {
    return {
      title:
        "Sua entrada está mais madura do que sua operação.",
      text:
        "Sua empresa já possui uma base capaz de gerar percepção e oportunidades, mas o caminho depois que o cliente demonstra interesse ainda apresenta perdas. Antes de aumentar muito a aquisição, existe espaço para organizar atendimento, acompanhamento e operação."
    };
  }

  if (
    presence < 60 &&
    authority < 60 &&
    conversion >= 70 &&
    operation >= 70
  ) {
    return {
      title:
        "Sua estrutura interna está à frente da sua presença digital.",
      text:
        "A empresa parece preparada para receber e conduzir oportunidades, mas ainda precisa ser encontrada e percebida com mais força. O próximo salto tende a vir de presença, posicionamento e autoridade."
    };
  }

  if (
    presence >= 70 &&
    authority < 60
  ) {
    return {
      title:
        "Você consegue aparecer, mas ainda pode convencer melhor.",
      text:
        "Ser encontrado já não parece ser o maior problema. A principal oportunidade está em transformar essa visibilidade em confiança através de apresentação profissional, provas e diferenciais claros."
    };
  }

  if (
    conversion < 60 &&
    operation < 60
  ) {
    return {
      title:
        "Existem oportunidades escapando depois do primeiro contato.",
      text:
        "Conversão e operação estão abaixo dos demais pilares. Isso normalmente indica dependência manual, pouca continuidade comercial ou falta de uma visão clara sobre cada oportunidade."
    };
  }

  if (
    presence < 60 &&
    authority >= 70
  ) {
    return {
      title:
        "Quem encontra sua empresa tende a confiar — o desafio é ser encontrado.",
      text:
        "Sua percepção de autoridade é superior à sua capacidade atual de aparecer. Existe uma oportunidade clara de colocar uma boa empresa diante de mais pessoas que já procuram pelo que você oferece."
    };
  }

  return {
    title: `${pillarVisual[strongestKey].short} é seu ponto mais forte; ${pillarVisual[primaryGap].short} merece prioridade.`,
    text:
      "Sua estrutura possui níveis diferentes de maturidade entre os quatro pilares. O maior ganho agora tende a vir menos de fazer tudo ao mesmo tempo e mais de corrigir o ponto que está limitando o restante da jornada."
  };
};

const buildDiegoReading = (
  scores,
  primaryGap,
  goal
) => {
  const gap =
    pillarVisual[primaryGap]?.name ||
    "estrutura digital";

  if (
    scores.presence >= 70 &&
    scores.authority >= 70 &&
    scores.operation < 60
  ) {
    return `Eu não começaria simplesmente colocando mais pessoas na entrada. Sua empresa já demonstra uma boa base de presença e confiança. Eu organizaria primeiro a operação que recebe essas oportunidades, para depois acelerar aquisição sem aumentar desperdício.`;
  }

  if (
    scores.presence < 60 &&
    scores.operation >= 70
  ) {
    return `Sua operação parece mais preparada do que sua capacidade atual de gerar demanda. Eu concentraria os próximos esforços em ser encontrado e construir uma presença mais consistente antes de adicionar novas camadas operacionais.`;
  }

  if (primaryGap === "conversion") {
    return `Você não precisa necessariamente de mais contatos agora. Primeiro eu entenderia onde os contatos que já chegam deixam de avançar e criaria um caminho mais claro entre interesse, acompanhamento e decisão.`;
  }

  if (primaryGap === "authority") {
    return `Eu trabalharia para fazer o digital representar melhor a qualidade real da sua empresa. Quando percepção e entrega ficam desalinhadas, bons negócios acabam competindo por preço ou sendo ignorados antes mesmo da conversa.`;
  }

  if (primaryGap === "presence") {
    return `Antes de sofisticar demais a operação, eu garantiria que sua empresa apareça para quem já está procurando pelo que você vende. Uma boa estrutura comercial sem fluxo de oportunidades continua subutilizada.`;
  }

  return `O principal ponto que eu atacaria hoje é ${gap}. Seu objetivo declarado foi "${goal || "estruturar melhor o digital"}", então eu começaria pelo gargalo que mais interfere nessa direção e só depois avançaria para a próxima camada.`;
};

const DigitalDiagnostic = () => {
  const [step, setStep] = useState(0);
  const [loadingResult, setLoadingResult] = useState(false);

  // KORAX DIAGNOSTIC JOURNEY V2
  const [pillarTransition, setPillarTransition] = useState(null);

  const [form, setForm] = useState({
    name: "",
    businessName: "",
    segment: "",

    presenceSearch: null,
    presenceStructure: null,

    authorityProof: null,
    authorityPresentation: null,

    conversionProcess: null,
    conversionFollowup: null,

    operationOrganization: null,
    operationManual: null,

    goal: "",
    phone: ""
  });

  const current = steps[step];

  const diagnosticMeta = {
    presenceSearch: {
      pillar: "Presença Digital",
      short: "Presença",
      question: 1,
      pillarIndex: 0,
      accent: "#16A34A",
      soft: "#F0FDF4",
      icon: "⌕"
    },
    presenceStructure: {
      pillar: "Presença Digital",
      short: "Presença",
      question: 2,
      pillarIndex: 0,
      accent: "#16A34A",
      soft: "#F0FDF4",
      icon: "⌕"
    },

    authorityProof: {
      pillar: "Autoridade Digital",
      short: "Autoridade",
      question: 3,
      pillarIndex: 1,
      accent: "#7C3AED",
      soft: "#F5F3FF",
      icon: "✦"
    },
    authorityPresentation: {
      pillar: "Autoridade Digital",
      short: "Autoridade",
      question: 4,
      pillarIndex: 1,
      accent: "#7C3AED",
      soft: "#F5F3FF",
      icon: "✦"
    },

    conversionProcess: {
      pillar: "Conversão Digital",
      short: "Conversão",
      question: 5,
      pillarIndex: 2,
      accent: "#D97706",
      soft: "#FFFBEB",
      icon: "↗"
    },
    conversionFollowup: {
      pillar: "Conversão Digital",
      short: "Conversão",
      question: 6,
      pillarIndex: 2,
      accent: "#D97706",
      soft: "#FFFBEB",
      icon: "↗"
    },

    operationOrganization: {
      pillar: "Operação Digital",
      short: "Operação",
      question: 7,
      pillarIndex: 3,
      accent: "#0284C7",
      soft: "#F0F9FF",
      icon: "⚙"
    },
    operationManual: {
      pillar: "Operação Digital",
      short: "Operação",
      question: 8,
      pillarIndex: 3,
      accent: "#0284C7",
      soft: "#F0F9FF",
      icon: "⚙"
    }
  };

  const currentDiagnosticMeta =
    diagnosticMeta[current] || null;

  const pillarNames = [
    "Presença",
    "Autoridade",
    "Conversão",
    "Operação"
  ];

  const preparationSteps = [
    "name",
    "business",
    "segment"
  ];

  const finishingSteps = [
    "goal",
    "phone"
  ];

  const currentSectionLabel = currentDiagnosticMeta
    ? currentDiagnosticMeta.pillar
    : preparationSteps.includes(current)
    ? "Conhecendo seu negócio"
    : finishingSteps.includes(current)
    ? "Finalizando seu diagnóstico"
    : "Diagnóstico de Estrutura Digital";

  const progress = useMemo(() => {
    if (step <= 0) return 0;
    if (step >= 14) return 100;

    return Math.round((step / 13) * 100);
  }, [step]);

  const scores = useMemo(() => {
    const normalize = value => {
      if (value === null || value === undefined) return 0;

      const map = {
        0: 20,
        1: 45,
        2: 70,
        3: 95
      };

      return map[value] ?? 0;
    };

    const average = (first, second) =>
      Math.round(
        (normalize(first) + normalize(second)) / 2
      );

    return {
      presence: average(
        form.presenceSearch,
        form.presenceStructure
      ),

      authority: average(
        form.authorityProof,
        form.authorityPresentation
      ),

      conversion: average(
        form.conversionProcess,
        form.conversionFollowup
      ),

      operation: average(
        form.operationOrganization,
        form.operationManual
      )
    };
  }, [form]);

  const overallScore = useMemo(() => {
    const values = Object.values(scores);
    return Math.round(
      values.reduce((total, value) => total + value, 0) /
        values.length
    );
  }, [scores]);

  const primaryGap = useMemo(() => {
    return Object.entries(scores).sort((a, b) => a[1] - b[1])[0][0];
  }, [scores]);

  const setValue = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const chooseAndNext = (field, value) => {
    setValue(field, value);

    const transitions = {
      presenceStructure: {
        pillar: "Presença analisada",
        text:
          "Já entendi como sua empresa é encontrada e qual estrutura sustenta sua presença digital.",
        next:
          "Agora vamos avaliar a confiança que ela transmite.",
        accent: "#22C55E",
        icon: "✓"
      },

      authorityPresentation: {
        pillar: "Autoridade analisada",
        text:
          "Já consigo entender como sua empresa constrói confiança antes do primeiro contato.",
        next:
          "Agora quero olhar o que acontece quando uma oportunidade chega.",
        accent: "#A78BFA",
        icon: "✓"
      },

      conversionFollowup: {
        pillar: "Conversão analisada",
        text:
          "Já identifiquei como os contatos são recebidos e o que acontece com quem ainda não compra.",
        next:
          "Agora vamos analisar como sua operação funciona por trás disso.",
        accent: "#FBBF24",
        icon: "✓"
      },

      operationManual: {
        pillar: "Operação analisada",
        text:
          "Agora eu já tenho uma visão dos quatro pilares da sua estrutura digital.",
        next:
          "Falta apenas entender sua principal prioridade.",
        accent: "#38BDF8",
        icon: "✓"
      }
    };

    const transition = transitions[field];

    if (transition) {
      setPillarTransition(transition);

      setTimeout(() => {
        setPillarTransition(null);
        setStep(prev => prev + 1);
      }, 1050);

      return;
    }

    setTimeout(() => {
      setStep(prev => prev + 1);
    }, 180);
  };

  const next = () => {
    setStep(prev => prev + 1);
  };

  const back = () => {
    setStep(prev => Math.max(0, prev - 1));
  };

  const finish = () => {
    const digits = form.phone.replace(/\D/g, "");

    if (digits.length < 10) {
      return;
    }

    setLoadingResult(true);
    setStep(14);

    setTimeout(() => {
      setLoadingResult(false);
      setStep(15);
    }, 1500);
  };

  const renderChoice = (label, field, value) => {
    const accent =
      currentDiagnosticMeta?.accent ||
      "#2563EB";

    return (
      <Button
        key={`${field}-${value}`}
        variant="outlined"
        onClick={() =>
          chooseAndNext(field, value)
        }
        onMouseEnter={event => {
          event.currentTarget.style.borderColor =
            accent;

          event.currentTarget.style.background =
            currentDiagnosticMeta?.soft ||
            "#F8FAFC";

          event.currentTarget.style.transform =
            "translateY(-1px)";
        }}
        onMouseLeave={event => {
          event.currentTarget.style.borderColor =
            "#E2E8F0";

          event.currentTarget.style.background =
            "#FFFFFF";

          event.currentTarget.style.transform =
            "translateY(0)";
        }}
        style={{
          width: "100%",
          minHeight: 68,
          justifyContent: "flex-start",
          textAlign: "left",
          borderRadius: 15,
          padding: "13px 14px",
          marginBottom: 10,
          border: "1px solid #E2E8F0",
          color: "#0F172A",
          background: "#FFFFFF",
          textTransform: "none",
          transition:
            "all .18s ease",
          boxShadow:
            "0 3px 10px rgba(15,23,42,.025)"
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          style={{
            width: "100%",
            gap: 12
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              width: 30,
              minWidth: 30,
              height: 30,
              borderRadius: 9,
              background:
                currentDiagnosticMeta?.soft ||
                "#EFF6FF",
              color: accent,
              fontSize: 12,
              fontWeight: 900,
              border: `1px solid ${accent}22`
            }}
          >
            {String.fromCharCode(
              65 + (3 - Number(value))
            )}
          </Box>

          <Typography
            style={{
              flex: 1,
              color: "#0F172A",
              fontSize: 14,
              fontWeight: 750,
              lineHeight: 1.4
            }}
          >
            {label}
          </Typography>

          <Typography
            style={{
              color: "#CBD5E1",
              fontSize: 17,
              fontWeight: 700
            }}
          >
            →
          </Typography>
        </Box>
      </Button>
    );
  };

  const renderHeader = () => {
    if (
      current === "start" ||
      current === "result" ||
      current === "processing" ||
      pillarTransition
    ) {
      return null;
    }

    return (
      <Box mb={3}>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          style={{
            gap: 14,
            marginBottom: 10
          }}
        >
          <Box>
            <Typography
              style={{
                color: "#94A3B8",
                fontSize: 9.5,
                fontWeight: 850,
                letterSpacing: ".09em",
                textTransform: "uppercase"
              }}
            >
              {currentDiagnosticMeta
                ? `Pergunta ${currentDiagnosticMeta.question} de 8`
                : `Etapa ${Math.min(step, 13)} de 13`}
            </Typography>

            <Typography
              style={{
                color:
                  currentDiagnosticMeta?.accent ||
                  "#475569",
                fontSize: 12,
                fontWeight: 900,
                marginTop: 3
              }}
            >
              {currentSectionLabel}
            </Typography>
          </Box>

          <Box
            style={{
              padding: "5px 8px",
              borderRadius: 999,
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              color: "#64748B",
              fontSize: 10,
              fontWeight: 850
            }}
          >
            {progress}%
          </Box>
        </Box>

        <Box
          style={{
            position: "relative",
            width: "100%",
            height: 6,
            overflow: "hidden",
            borderRadius: 999,
            background: "#E8EDF4"
          }}
        >
          <Box
            style={{
              width: `${progress}%`,
              height: "100%",
              borderRadius: 999,
              background:
                currentDiagnosticMeta?.accent ||
                "#2563EB",
              transition:
                "width .35s ease, background .25s ease"
            }}
          />
        </Box>

        {currentDiagnosticMeta && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{
              gap: 5,
              marginTop: 11
            }}
          >
            {pillarNames.map(
              (pillar, index) => {
                const active =
                  index ===
                  currentDiagnosticMeta.pillarIndex;

                const complete =
                  index <
                  currentDiagnosticMeta.pillarIndex;

                return (
                  <Box
                    key={pillar}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      flex: 1,
                      minHeight: 27,
                      borderRadius: 8,
                      background: active
                        ? currentDiagnosticMeta.soft
                        : complete
                        ? "#F0FDF4"
                        : "#F8FAFC",
                      color: active
                        ? currentDiagnosticMeta.accent
                        : complete
                        ? "#16A34A"
                        : "#94A3B8",
                      border: active
                        ? `1px solid ${currentDiagnosticMeta.accent}30`
                        : "1px solid #EEF2F7",
                      fontSize: 8.5,
                      fontWeight: 850
                    }}
                  >
                    {complete
                      ? "✓ "
                      : ""}
                    {pillar}
                  </Box>
                );
              }
            )}
          </Box>
        )}
      </Box>
    );
  };

  const QuestionTitle = ({
    children,
    subtitle
  }) => (
    <>
      {currentDiagnosticMeta && (
        <Box
          display="inline-flex"
          alignItems="center"
          style={{
            gap: 7,
            padding: "6px 9px",
            marginBottom: 15,
            borderRadius: 9,
            color:
              currentDiagnosticMeta.accent,
            background:
              currentDiagnosticMeta.soft,
            border:
              `1px solid ${currentDiagnosticMeta.accent}22`
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              width: 20,
              height: 20,
              borderRadius: 6,
              color: "#FFFFFF",
              background:
                currentDiagnosticMeta.accent,
              fontSize: 10,
              fontWeight: 900
            }}
          >
            {currentDiagnosticMeta.icon}
          </Box>

          <Typography
            style={{
              fontSize: 9,
              fontWeight: 900,
              letterSpacing: ".06em",
              textTransform: "uppercase",
              color:
                currentDiagnosticMeta.accent
            }}
          >
            {currentDiagnosticMeta.pillar}
          </Typography>
        </Box>
      )}

      <Typography
        style={{
          fontSize:
            "clamp(24px, 5vw, 31px)",
          lineHeight: 1.12,
          fontWeight: 900,
          letterSpacing: "-.8px",
          color: "#0F172A",
          marginBottom:
            subtitle ? 11 : 25
        }}
      >
        {children}
      </Typography>

      {subtitle && (
        <Typography
          style={{
            fontSize: 13.5,
            lineHeight: 1.6,
            color: "#64748B",
            marginBottom: 25,
            maxWidth: 520
          }}
        >
          {subtitle}
        </Typography>
      )}
    </>
  );

  const renderPillarTransition = () => {
    if (!pillarTransition) {
      return null;
    }

    return (
      <Box
        textAlign="center"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        style={{
          minHeight: 430,
          padding: "24px 4px"
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{
            width: 68,
            height: 68,
            borderRadius: 21,
            color: "#06101F",
            background:
              pillarTransition.accent,
            fontSize: 28,
            fontWeight: 950,
            boxShadow:
              `0 15px 45px ${pillarTransition.accent}30`
          }}
        >
          {pillarTransition.icon}
        </Box>

        <Typography
          style={{
            color: "#FFFFFF",
            fontSize:
              "clamp(25px,5vw,32px)",
            fontWeight: 950,
            letterSpacing: "-.04em",
            lineHeight: 1.1,
            marginTop: 23
          }}
        >
          {pillarTransition.pillar}
        </Typography>

        <Typography
          style={{
            color:
              "rgba(219,234,254,.72)",
            fontSize: 13.5,
            lineHeight: 1.65,
            maxWidth: 460,
            marginTop: 13
          }}
        >
          {pillarTransition.text}
        </Typography>

        <Box
          style={{
            width: 54,
            height: 3,
            borderRadius: 999,
            background:
              pillarTransition.accent,
            margin: "21px 0"
          }}
        />

        <Typography
          style={{
            color: "#FFFFFF",
            fontSize: 12,
            lineHeight: 1.55,
            fontWeight: 750,
            maxWidth: 420
          }}
        >
          {pillarTransition.next}
        </Typography>
      </Box>
    );
  };

  const renderBody = () => {
    switch (current) {
      case "start":
        return (
          <Box
            textAlign="center"
            style={{
              color: "#FFFFFF"
            }}
          >
            <Box
              mx="auto"
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                width: 58,
                height: 58,
                borderRadius: 18,
                background:
                  "linear-gradient(135deg,#2563EB,#38BDF8)",
                color: "#FFFFFF",
                fontWeight: 950,
                fontSize: 20,
                boxShadow:
                  "0 13px 40px rgba(37,99,235,.32)"
              }}
            >
              DM
            </Box>

            <Typography
              style={{
                marginTop: 12,
                color: "#BFDBFE",
                fontSize: 9.5,
                fontWeight: 900,
                letterSpacing: ".12em",
                textTransform: "uppercase"
              }}
            >
              Diego Metaforma
            </Typography>

            <Typography
              style={{
                maxWidth: 530,
                margin: "24px auto 0",
                color: "#FFFFFF",
                fontSize:
                  "clamp(31px,7vw,44px)",
                lineHeight: 1.03,
                fontWeight: 950,
                letterSpacing: "-.055em"
              }}
            >
              Descubra onde sua empresa está{" "}
              <span
                style={{
                  color: "#38BDF8"
                }}
              >
                perdendo oportunidades
              </span>{" "}
              no digital.
            </Typography>

            <Typography
              style={{
                maxWidth: 480,
                margin: "19px auto 0",
                color:
                  "rgba(219,234,254,.75)",
                fontSize: 13.5,
                lineHeight: 1.65
              }}
            >
              Vou analisar os quatro pilares que sustentam sua estrutura digital e mostrar onde concentrar seus próximos esforços.
            </Typography>

            <Box
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(3, minmax(0,1fr))",
                gap: 7,
                margin: "24px auto",
                maxWidth: 500
              }}
            >
              {[
                ["≈ 3 min", "Rápido"],
                ["4 pilares", "Diagnóstico"],
                ["100%", "Personalizado"]
              ].map(([value, label]) => (
                <Box
                  key={label}
                  style={{
                    padding: "11px 7px",
                    borderRadius: 12,
                    background:
                      "rgba(255,255,255,.065)",
                    border:
                      "1px solid rgba(255,255,255,.10)"
                  }}
                >
                  <Typography
                    style={{
                      color: "#FFFFFF",
                      fontSize: 12,
                      fontWeight: 900
                    }}
                  >
                    {value}
                  </Typography>

                  <Typography
                    style={{
                      color:
                        "rgba(219,234,254,.55)",
                      fontSize: 8.5,
                      marginTop: 3
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={next}
              style={{
                maxWidth: 470,
                minHeight: 56,
                borderRadius: 14,
                textTransform: "none",
                fontSize: 14,
                fontWeight: 900,
                background:
                  "linear-gradient(135deg,#2563EB,#0EA5E9)",
                color: "#FFFFFF",
                boxShadow:
                  "0 13px 30px rgba(37,99,235,.28)"
              }}
            >
              Começar meu diagnóstico →
            </Button>

            <Typography
              style={{
                marginTop: 13,
                color:
                  "rgba(191,219,254,.46)",
                fontSize: 9.5
              }}
            >
              Gratuito • suas respostas são usadas para gerar sua análise
            </Typography>
          </Box>
        );

      case "name":
        return (
          <>
            <QuestionTitle>
              Antes de começar, como posso te chamar?
            </QuestionTitle>

            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              placeholder="Seu primeiro nome"
              value={form.name}
              onChange={e => setValue("name", e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && form.name.trim()) next();
              }}
            />

            <MainNextButton
              disabled={!form.name.trim()}
              onClick={next}
            />
          </>
        );

      case "business":
        return (
          <>
            <QuestionTitle
              subtitle={`Perfeito${
                form.name ? `, ${form.name.split(" ")[0]}` : ""
              }. Agora quero entender um pouco sobre a sua empresa.`}
            >
              Qual é o nome do seu negócio?
            </QuestionTitle>

            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              placeholder="Nome da empresa"
              value={form.businessName}
              onChange={e =>
                setValue("businessName", e.target.value)
              }
              onKeyDown={e => {
                if (
                  e.key === "Enter" &&
                  form.businessName.trim()
                ) {
                  next();
                }
              }}
            />

            <MainNextButton
              disabled={!form.businessName.trim()}
              onClick={next}
            />
          </>
        );

      case "segment":
        return (
          <>
            <QuestionTitle>
              Em qual mercado sua empresa atua?
            </QuestionTitle>

            {[
              "Saúde e estética",
              "Serviços profissionais",
              "Alimentação",
              "Varejo e comércio",
              "Imobiliário",
              "Educação",
              "Automotivo",
              "Outro"
            ].map(option =>
              renderChoice(option, "segment", option)
            )}
          </>
        );

      case "presenceSearch":
        return (
          <>
            <QuestionTitle
              subtitle="Pense no que acontece quando uma pessoa procura no Google pelo serviço ou produto que você oferece na sua região."
            >
              Quando alguém pesquisa pelo que sua empresa vende, com que facilidade vocês aparecem?
            </QuestionTitle>

            {renderChoice(
              "Aparecemos muito bem nas principais buscas",
              "presenceSearch",
              3
            )}

            {renderChoice(
              "Aparecemos em algumas buscas, mas ainda podemos melhorar",
              "presenceSearch",
              2
            )}

            {renderChoice(
              "Aparecemos pouco e dependemos bastante de outros canais",
              "presenceSearch",
              1
            )}

            {renderChoice(
              "Praticamente não aparecemos nas buscas",
              "presenceSearch",
              0
            )}
          </>
        );

      case "presenceStructure":
        return (
          <>
            <QuestionTitle
              subtitle="Agora quero entender a estrutura que sustenta sua presença online."
            >
              Além das redes sociais, qual estrutura digital sua empresa possui hoje?
            </QuestionTitle>

            {renderChoice(
              "Temos uma estrutura completa: site, Google e canais bem organizados",
              "presenceStructure",
              3
            )}

            {renderChoice(
              "Temos parte da estrutura, mas alguns canais ainda precisam melhorar",
              "presenceStructure",
              2
            )}

            {renderChoice(
              "Temos basicamente redes sociais e WhatsApp",
              "presenceStructure",
              1
            )}

            {renderChoice(
              "Nossa estrutura digital ainda é muito limitada",
              "presenceStructure",
              0
            )}
          </>
        );

      case "authorityProof":
        return (
          <>
            <QuestionTitle
              subtitle="Provas reduzem a insegurança de quem ainda não conhece sua empresa."
            >
              Que tipo de prova um potencial cliente encontra antes de decidir comprar de vocês?
            </QuestionTitle>

            {renderChoice(
              "Muitas provas: avaliações, depoimentos, resultados e conteúdo",
              "authorityProof",
              3
            )}

            {renderChoice(
              "Temos algumas boas provas, mas poderíamos mostrá-las melhor",
              "authorityProof",
              2
            )}

            {renderChoice(
              "Temos poucas provas que aumentem a confiança",
              "authorityProof",
              1
            )}

            {renderChoice(
              "Praticamente não temos prova social estruturada",
              "authorityProof",
              0
            )}
          </>
        );

      case "authorityPresentation":
        return (
          <>
            <QuestionTitle
              subtitle="Pense na impressão que alguém teria comparando vocês com os melhores concorrentes."
            >
              Como você acredita que sua empresa é percebida no digital?
            </QuestionTitle>

            {renderChoice(
              "Estamos entre as empresas que passam mais profissionalismo",
              "authorityPresentation",
              3
            )}

            {renderChoice(
              "Temos uma boa imagem, mas ainda podemos evoluir",
              "authorityPresentation",
              2
            )}

            {renderChoice(
              "Nossa apresentação ainda fica atrás dos melhores concorrentes",
              "authorityPresentation",
              1
            )}

            {renderChoice(
              "Nossa presença digital ainda não representa a qualidade real da empresa",
              "authorityPresentation",
              0
            )}
          </>
        );

      case "conversionProcess":
        return (
          <>
            <QuestionTitle
              subtitle="Agora vamos olhar o que acontece depois que alguém demonstra interesse."
            >
              Quando um potencial cliente entra em contato, o que normalmente acontece?
            </QuestionTitle>

            {renderChoice(
              "Temos um processo claro: respondemos, qualificamos e conduzimos até uma decisão",
              "conversionProcess",
              3
            )}

            {renderChoice(
              "Respondemos bem, mas parte do processo ainda é manual",
              "conversionProcess",
              2
            )}

            {renderChoice(
              "Às vezes demoramos ou alguns contatos acabam se perdendo",
              "conversionProcess",
              1
            )}

            {renderChoice(
              "Não temos um processo comercial bem definido",
              "conversionProcess",
              0
            )}
          </>
        );

      case "conversionFollowup":
        return (
          <>
            <QuestionTitle
              subtitle="Nem todo cliente compra no primeiro contato. Quero entender o que acontece com essas oportunidades."
            >
              Quando alguém demonstra interesse, mas não compra naquele momento, como vocês acompanham essa pessoa?
            </QuestionTitle>

            {renderChoice(
              "Temos acompanhamento organizado e sabemos quando retornar",
              "conversionFollowup",
              3
            )}

            {renderChoice(
              "Acompanhamos alguns casos manualmente",
              "conversionFollowup",
              2
            )}

            {renderChoice(
              "Normalmente depende de alguém lembrar de fazer o retorno",
              "conversionFollowup",
              1
            )}

            {renderChoice(
              "Na maioria das vezes não fazemos acompanhamento",
              "conversionFollowup",
              0
            )}
          </>
        );

      case "operationOrganization":
        return (
          <>
            <QuestionTitle
              subtitle="Quero entender se existe uma visão organizada de tudo que está acontecendo."
            >
              Hoje vocês conseguem saber rapidamente em que situação está cada oportunidade ou cliente?
            </QuestionTitle>

            {renderChoice(
              "Sim, temos tudo organizado em sistema ou CRM",
              "operationOrganization",
              3
            )}

            {renderChoice(
              "Em partes, usamos algumas ferramentas para nos organizar",
              "operationOrganization",
              2
            )}

            {renderChoice(
              "É bastante espalhado entre WhatsApp, agenda e anotações",
              "operationOrganization",
              1
            )}

            {renderChoice(
              "Não temos uma visão organizada dos contatos",
              "operationOrganization",
              0
            )}
          </>
        );

      case "operationManual":
        return (
          <>
            <QuestionTitle
              subtitle="Considere atendimento, agendamento, acompanhamento e tarefas repetitivas."
            >
              Quanto da sua operação depende de alguém estar disponível para executar tarefas manualmente?
            </QuestionTitle>

            {renderChoice(
              "Muito pouco, temos processos e automações bem estruturados",
              "operationManual",
              3
            )}

            {renderChoice(
              "Parcialmente, algumas partes já são organizadas ou automatizadas",
              "operationManual",
              2
            )}

            {renderChoice(
              "Bastante, grande parte ainda depende da equipe",
              "operationManual",
              1
            )}

            {renderChoice(
              "Praticamente tudo depende de alguém estar disponível",
              "operationManual",
              0
            )}
          </>
        );

      case "goal":
        return (
          <>
            <QuestionTitle>
              Qual é sua maior prioridade hoje?
            </QuestionTitle>

            {[
              "Ser encontrado por mais clientes",
              "Melhorar presença e autoridade",
              "Converter melhor os contatos que já chegam",
              "Automatizar atendimento e agendamento",
              "Estruturar tudo isso de forma profissional"
            ].map(option =>
              renderChoice(option, "goal", option)
            )}
          </>
        );

      case "phone":
        return (
          <>
            <QuestionTitle
              subtitle="Seu número será utilizado para identificar seu diagnóstico e, caso você solicite, continuar a conversa pelo WhatsApp."
            >
              Seu diagnóstico está quase pronto. Qual é o seu WhatsApp?
            </QuestionTitle>

            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              placeholder="(11) 99999-9999"
              value={form.phone}
              onChange={e =>
                setValue("phone", e.target.value)
              }
              onKeyDown={e => {
                if (
                  e.key === "Enter" &&
                  form.phone.replace(/\D/g, "").length >= 10
                ) {
                  finish();
                }
              }}
            />

            <Button
              fullWidth
              variant="contained"
              disableElevation
              disabled={
                form.phone.replace(/\D/g, "").length < 10
              }
              onClick={finish}
              style={{
                minHeight: 54,
                borderRadius: 14,
                marginTop: 18,
                textTransform: "none",
                fontWeight: 700,
                fontSize: 16,
                background:
                  form.phone.replace(/\D/g, "").length >= 10
                    ? COLORS.primary
                    : "#d0d5dd",
                color: "#fff"
              }}
            >
              Ver meu diagnóstico
            </Button>

            <Typography
              style={{
                marginTop: 14,
                fontSize: 11,
                lineHeight: 1.5,
                color: "#98a2b3"
              }}
            >
              Ao continuar, você concorda em utilizar as informações
              fornecidas para gerar seu diagnóstico e permitir o contato
              relacionado a ele.
            </Typography>
          </>
        );

      case "processing":
        return (
          <Box
            textAlign="center"
            style={{
              color: "#FFFFFF",
              padding: "38px 4px"
            }}
          >
            {loadingResult && (
              <CircularProgress
                size={44}
                thickness={4}
                style={{
                  color: "#38BDF8"
                }}
              />
            )}

            <Typography
              style={{
                color: "#FFFFFF",
                fontSize:
                  "clamp(25px,5vw,32px)",
                lineHeight: 1.1,
                fontWeight: 950,
                letterSpacing: "-.04em",
                marginTop: 25
              }}
            >
              Cruzando suas respostas...
            </Typography>

            <Typography
              style={{
                color:
                  "rgba(219,234,254,.65)",
                fontSize: 12.5,
                lineHeight: 1.6,
                margin: "10px auto 25px",
                maxWidth: 420
              }}
            >
              Estou comparando seus quatro pilares para identificar forças, gargalos e prioridades.
            </Typography>

            <Box
              style={{
                maxWidth: 390,
                margin: "0 auto",
                display: "grid",
                gap: 7
              }}
            >
              {[
                ["✓", "Presença Digital"],
                ["✓", "Autoridade Digital"],
                ["✓", "Conversão Digital"],
                ["✓", "Operação Digital"]
              ].map(([icon, label]) => (
                <Box
                  key={label}
                  display="flex"
                  alignItems="center"
                  style={{
                    padding: "10px 12px",
                    borderRadius: 11,
                    background:
                      "rgba(255,255,255,.06)",
                    border:
                      "1px solid rgba(255,255,255,.09)"
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 7,
                      background:
                        "rgba(74,222,128,.12)",
                      color: "#4ADE80",
                      fontSize: 10,
                      fontWeight: 900,
                      marginRight: 9
                    }}
                  >
                    {icon}
                  </Box>

                  <Typography
                    style={{
                      color:
                        "rgba(255,255,255,.82)",
                      fontSize: 11,
                      fontWeight: 750
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );

      case "result": {
        const sortedScores = Object.entries(
          scores
        ).sort(
          (a, b) => a[1] - b[1]
        );

        const strongestKey =
          [...sortedScores]
            .sort(
              (a, b) =>
                b[1] - a[1]
            )[0][0];

        const opportunityKey =
          sortedScores[1]?.[0] ||
          primaryGap;

        const strongestScore =
          scores[strongestKey];

        const gapScore =
          scores[primaryGap];

        const opportunityScore =
          scores[opportunityKey];

        const maturity =
          getMaturity(
            overallScore
          );

        const executive =
          buildExecutiveReading(
            scores,
            strongestKey,
            primaryGap
          );

        const priorities =
          buildPriorities(form);

        const diegoReading =
          buildDiegoReading(
            scores,
            primaryGap,
            form.goal
          );

        const radarRadius = 72;
        const radarCenter = 90;

        const radarPoints = [
          [
            radarCenter,
            radarCenter -
              radarRadius *
                (scores.presence / 100)
          ],
          [
            radarCenter +
              radarRadius *
                (scores.authority / 100),
            radarCenter
          ],
          [
            radarCenter,
            radarCenter +
              radarRadius *
                (scores.conversion / 100)
          ],
          [
            radarCenter -
              radarRadius *
                (scores.operation / 100),
            radarCenter
          ]
        ]
          .map(point =>
            point.join(",")
          )
          .join(" ");

        const whatsappText =
          encodeURIComponent(
            `Oi Diego, fiz o Diagnóstico de Estrutura Digital.

Nome: ${form.name}
Empresa: ${form.businessName}
Resultado: ${overallScore}/100
Nível: ${maturity.label}
Principal gargalo: ${pillarVisual[primaryGap].name} (${gapScore}/100)
Meu objetivo: ${form.goal}

Quero conversar sobre o meu diagnóstico.`
          );

        return (
          <Box>
            {/* =================================================
                HERO DO RELATÓRIO
            ================================================== */}
            <Box
              textAlign="center"
              style={{
                position: "relative",
                overflow: "hidden",
                margin: "-30px -24px 25px",
                padding: "34px 22px 29px",
                color: "#FFFFFF",
                background:
                  "radial-gradient(circle at 80% 10%, rgba(37,99,235,.34), transparent 35%), linear-gradient(145deg,#030A14,#071426 55%,#0A1D3C)"
              }}
            >
              <Typography
                style={{
                  color: "#93C5FD",
                  fontSize: 9.5,
                  fontWeight: 900,
                  letterSpacing: ".12em",
                  textTransform: "uppercase"
                }}
              >
                Diagnóstico de Estrutura Digital
              </Typography>

              <Typography
                style={{
                  color:
                    "rgba(219,234,254,.65)",
                  fontSize: 10,
                  marginTop: 5
                }}
              >
                {form.businessName}
              </Typography>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{
                  margin:
                    "25px auto 17px"
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  style={{
                    width: 168,
                    height: 168,
                    borderRadius: "50%",
                    background: `conic-gradient(#38BDF8 0 ${overallScore}%, rgba(255,255,255,.09) ${overallScore}% 100%)`,
                    boxShadow:
                      "0 22px 55px rgba(14,165,233,.16)"
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    style={{
                      width: 137,
                      height: 137,
                      borderRadius:
                        "50%",
                      background:
                        "#071426"
                    }}
                  >
                    <Typography
                      style={{
                        color:
                          "#FFFFFF",
                        fontSize: 51,
                        lineHeight: 1,
                        fontWeight: 950,
                        letterSpacing:
                          "-.06em"
                      }}
                    >
                      {overallScore}
                    </Typography>

                    <Typography
                      style={{
                        color:
                          "rgba(219,234,254,.55)",
                        fontSize: 9.5,
                        marginTop: 4
                      }}
                    >
                      de 100
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                display="inline-flex"
                alignItems="center"
                style={{
                  padding:
                    "7px 11px",
                  borderRadius: 999,
                  background:
                    `${maturity.color}20`,
                  border:
                    `1px solid ${maturity.color}55`,
                  color:
                    maturity.color ===
                    "#D97706"
                      ? "#FBBF24"
                      : maturity.color ===
                        "#EA580C"
                      ? "#FB923C"
                      : maturity.color ===
                        "#DC2626"
                      ? "#F87171"
                      : "#86EFAC",
                  fontSize: 9.5,
                  fontWeight: 900,
                  letterSpacing:
                    ".045em",
                  textTransform:
                    "uppercase"
                }}
              >
                {maturity.label}
              </Box>

              <Typography
                style={{
                  maxWidth: 430,
                  margin:
                    "13px auto 0",
                  color:
                    "rgba(219,234,254,.66)",
                  fontSize: 11,
                  lineHeight: 1.6
                }}
              >
                Seu resultado representa o equilíbrio entre presença, autoridade, conversão e operação.
              </Typography>
            </Box>

            {/* =================================================
                RADAR
            ================================================== */}
            <Box
              style={{
                marginBottom: 16,
                padding: 19,
                borderRadius: 18,
                background: "#FFFFFF",
                border:
                  "1px solid #E5EAF1"
              }}
            >
              <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                style={{
                  gap: 10,
                  marginBottom: 12
                }}
              >
                <Box>
                  <Typography
                    style={{
                      color:
                        "#0F172A",
                      fontSize: 14,
                      fontWeight: 900
                    }}
                  >
                    Visão da sua estrutura
                  </Typography>

                  <Typography
                    style={{
                      color:
                        "#94A3B8",
                      fontSize: 10,
                      marginTop: 3
                    }}
                  >
                    Comparação entre os quatro pilares.
                  </Typography>
                </Box>
              </Box>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <svg
                  width="240"
                  height="240"
                  viewBox="0 0 180 180"
                  role="img"
                  aria-label="Radar dos quatro pilares"
                >
                  {[18, 36, 54, 72].map(
                    radius => (
                      <polygon
                        key={radius}
                        points={`
                          90,${90 - radius}
                          ${90 + radius},90
                          90,${90 + radius}
                          ${90 - radius},90
                        `}
                        fill="none"
                        stroke="#E2E8F0"
                        strokeWidth="1"
                      />
                    )
                  )}

                  <line
                    x1="90"
                    y1="18"
                    x2="90"
                    y2="162"
                    stroke="#E2E8F0"
                  />

                  <line
                    x1="18"
                    y1="90"
                    x2="162"
                    y2="90"
                    stroke="#E2E8F0"
                  />

                  <polygon
                    points={
                      radarPoints
                    }
                    fill="rgba(37,99,235,.18)"
                    stroke="#2563EB"
                    strokeWidth="2.2"
                  />

                  <text
                    x="90"
                    y="10"
                    textAnchor="middle"
                    fontSize="7.5"
                    fontWeight="700"
                    fill="#475569"
                  >
                    PRESENÇA {scores.presence}
                  </text>

                  <text
                    x="172"
                    y="92"
                    textAnchor="end"
                    fontSize="7"
                    fontWeight="700"
                    fill="#475569"
                  >
                    AUT. {scores.authority}
                  </text>

                  <text
                    x="90"
                    y="178"
                    textAnchor="middle"
                    fontSize="7"
                    fontWeight="700"
                    fill="#475569"
                  >
                    CONVERSÃO {scores.conversion}
                  </text>

                  <text
                    x="8"
                    y="92"
                    textAnchor="start"
                    fontSize="7"
                    fontWeight="700"
                    fill="#475569"
                  >
                    OP. {scores.operation}
                  </text>
                </svg>
              </Box>

              <Box
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(2,minmax(0,1fr))",
                  gap: 8,
                  marginTop: 5
                }}
              >
                {Object.entries(
                  scores
                ).map(
                  ([key, score]) => (
                    <Box
                      key={key}
                      style={{
                        padding:
                          "10px 11px",
                        borderRadius:
                          11,
                        background:
                          pillarVisual[
                            key
                          ].soft,
                        border:
                          `1px solid ${pillarVisual[key].color}18`
                      }}
                    >
                      <Typography
                        style={{
                          color:
                            pillarVisual[
                              key
                            ].color,
                          fontSize:
                            9,
                          fontWeight:
                            900
                        }}
                      >
                        {
                          pillarVisual[
                            key
                          ].short
                        }
                      </Typography>

                      <Typography
                        style={{
                          color:
                            "#0F172A",
                          fontSize:
                            17,
                          fontWeight:
                            950,
                          marginTop: 2
                        }}
                      >
                        {score}
                        <span
                          style={{
                            color:
                              "#94A3B8",
                            fontSize:
                              8,
                            fontWeight:
                              700
                          }}
                        >
                          /100
                        </span>
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            </Box>

            {/* =================================================
                FORÇA / GARGALO / OPORTUNIDADE
            ================================================== */}
            <Box
              style={{
                display: "grid",
                gap: 9,
                marginBottom: 16
              }}
            >
              <Box
                style={{
                  padding:
                    "15px 16px",
                  borderRadius: 15,
                  background:
                    pillarVisual[
                      strongestKey
                    ].soft,
                  border:
                    `1px solid ${pillarVisual[strongestKey].color}25`
                }}
              >
                <Typography
                  style={{
                    color:
                      pillarVisual[
                        strongestKey
                      ].color,
                    fontSize: 8.5,
                    fontWeight: 950,
                    letterSpacing:
                      ".08em",
                    textTransform:
                      "uppercase"
                  }}
                >
                  ✓ Seu ponto mais forte
                </Typography>

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{
                    marginTop: 6,
                    gap: 10
                  }}
                >
                  <Typography
                    style={{
                      color:
                        "#0F172A",
                      fontSize: 14,
                      fontWeight: 900
                    }}
                  >
                    {
                      pillarVisual[
                        strongestKey
                      ].name
                    }
                  </Typography>

                  <Typography
                    style={{
                      color:
                        "#0F172A",
                      fontSize: 15,
                      fontWeight: 950
                    }}
                  >
                    {strongestScore}
                  </Typography>
                </Box>
              </Box>

              <Box
                style={{
                  padding:
                    "15px 16px",
                  borderRadius: 15,
                  background:
                    "#FEF2F2",
                  border:
                    "1px solid #FECACA"
                }}
              >
                <Typography
                  style={{
                    color:
                      "#DC2626",
                    fontSize: 8.5,
                    fontWeight: 950,
                    letterSpacing:
                      ".08em",
                    textTransform:
                      "uppercase"
                  }}
                >
                  ⚠ Principal gargalo
                </Typography>

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{
                    marginTop: 6,
                    gap: 10
                  }}
                >
                  <Typography
                    style={{
                      color:
                        "#0F172A",
                      fontSize: 14,
                      fontWeight: 900
                    }}
                  >
                    {
                      pillarVisual[
                        primaryGap
                      ].name
                    }
                  </Typography>

                  <Typography
                    style={{
                      color:
                        "#DC2626",
                      fontSize: 15,
                      fontWeight: 950
                    }}
                  >
                    {gapScore}
                  </Typography>
                </Box>
              </Box>

              {opportunityKey !==
                primaryGap &&
                opportunityKey !==
                  strongestKey && (
                <Box
                  style={{
                    padding:
                      "15px 16px",
                    borderRadius:
                      15,
                    background:
                      "#EFF6FF",
                    border:
                      "1px solid #DBEAFE"
                  }}
                >
                  <Typography
                    style={{
                      color:
                        "#2563EB",
                      fontSize: 8.5,
                      fontWeight: 950,
                      letterSpacing:
                        ".08em",
                      textTransform:
                        "uppercase"
                    }}
                  >
                    → Próxima oportunidade
                  </Typography>

                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    style={{
                      marginTop: 6
                    }}
                  >
                    <Typography
                      style={{
                        color:
                          "#0F172A",
                        fontSize: 14,
                        fontWeight: 900
                      }}
                    >
                      {
                        pillarVisual[
                          opportunityKey
                        ].name
                      }
                    </Typography>

                    <Typography
                      style={{
                        color:
                          "#2563EB",
                        fontSize: 15,
                        fontWeight: 950
                      }}
                    >
                      {opportunityScore}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* =================================================
                LEITURA EXECUTIVA
            ================================================== */}
            <Box
              style={{
                padding:
                  "20px 18px",
                marginBottom: 16,
                borderRadius: 18,
                background:
                  "#F8FAFC",
                border:
                  "1px solid #E5EAF1"
              }}
            >
              <Typography
                style={{
                  color:
                    "#64748B",
                  fontSize: 8.5,
                  fontWeight: 950,
                  letterSpacing:
                    ".09em",
                  textTransform:
                    "uppercase"
                }}
              >
                Leitura executiva
              </Typography>

              <Typography
                style={{
                  color:
                    "#0F172A",
                  fontSize: 19,
                  lineHeight: 1.18,
                  fontWeight: 950,
                  letterSpacing:
                    "-.025em",
                  marginTop: 9
                }}
              >
                {executive.title}
              </Typography>

              <Typography
                style={{
                  color:
                    "#64748B",
                  fontSize: 12,
                  lineHeight: 1.7,
                  marginTop: 11
                }}
              >
                {executive.text}
              </Typography>
            </Box>

            {/* =================================================
                PRIORIDADES
            ================================================== */}
            <Box
              style={{
                marginBottom: 17
              }}
            >
              <Typography
                style={{
                  color:
                    "#0F172A",
                  fontSize: 16,
                  fontWeight: 950
                }}
              >
                Seu plano prioritário
              </Typography>

              <Typography
                style={{
                  color:
                    "#94A3B8",
                  fontSize: 10,
                  lineHeight: 1.5,
                  marginTop: 4,
                  marginBottom:
                    12
                }}
              >
                Com base nas respostas que mais impactaram seu resultado.
              </Typography>

              {priorities.map(
                (
                  priority,
                  index
                ) => (
                  <Box
                    key={
                      priority.id
                    }
                    display="flex"
                    alignItems="flex-start"
                    style={{
                      gap: 11,
                      padding:
                        "14px 0",
                      borderBottom:
                        index ===
                        priorities.length -
                          1
                          ? "none"
                          : "1px solid #EEF2F7"
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      style={{
                        width: 34,
                        minWidth: 34,
                        height: 34,
                        borderRadius:
                          10,
                        background:
                          "#071426",
                        color:
                          "#FFFFFF",
                        fontSize:
                          10,
                        fontWeight:
                          950
                      }}
                    >
                      {String(
                        index + 1
                      ).padStart(
                        2,
                        "0"
                      )}
                    </Box>

                    <Box>
                      <Typography
                        style={{
                          color:
                            "#0F172A",
                          fontSize:
                            12.5,
                          fontWeight:
                            900
                        }}
                      >
                        {
                          priority.title
                        }
                      </Typography>

                      <Typography
                        style={{
                          color:
                            "#64748B",
                          fontSize:
                            10.5,
                          lineHeight:
                            1.55,
                          marginTop: 4
                        }}
                      >
                        {
                          priority.description
                        }
                      </Typography>
                    </Box>
                  </Box>
                )
              )}
            </Box>

            {/* =================================================
                LEITURA DO DIEGO
            ================================================== */}
            <Box
              style={{
                padding:
                  "19px 18px",
                marginBottom: 17,
                borderRadius: 18,
                color: "#FFFFFF",
                background:
                  "linear-gradient(145deg,#071426,#0D2D63)",
                boxShadow:
                  "0 16px 40px rgba(15,43,94,.14)"
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                style={{
                  gap: 10,
                  marginBottom: 13
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius:
                      12,
                    background:
                      "linear-gradient(135deg,#2563EB,#38BDF8)",
                    color:
                      "#FFFFFF",
                    fontSize: 12,
                    fontWeight:
                      950
                  }}
                >
                  DM
                </Box>

                <Box>
                  <Typography
                    style={{
                      color:
                        "#FFFFFF",
                      fontSize: 11,
                      fontWeight: 900
                    }}
                  >
                    Minha leitura
                  </Typography>

                  <Typography
                    style={{
                      color:
                        "rgba(191,219,254,.55)",
                      fontSize: 8.5,
                      marginTop: 1
                    }}
                  >
                    Diego Metaforma
                  </Typography>
                </Box>
              </Box>

              <Typography
                style={{
                  color:
                    "rgba(239,246,255,.86)",
                  fontSize: 12,
                  lineHeight: 1.7,
                  fontStyle:
                    "italic"
                }}
              >
                “{diegoReading}”
              </Typography>
            </Box>

            {/* =================================================
                CTA
            ================================================== */}
            <Box
              style={{
                padding:
                  "20px 18px",
                borderRadius: 18,
                border:
                  "1px solid #DBEAFE",
                background:
                  "linear-gradient(180deg,#FFFFFF,#F8FBFF)"
              }}
            >
              <Typography
                style={{
                  color:
                    "#0F172A",
                  fontSize: 20,
                  lineHeight: 1.15,
                  fontWeight: 950,
                  letterSpacing:
                    "-.03em"
                }}
              >
                Quer transformar esse diagnóstico em um plano de ação?
              </Typography>

              <Typography
                style={{
                  color:
                    "#64748B",
                  fontSize: 11.5,
                  lineHeight: 1.65,
                  margin:
                    "9px 0 17px"
                }}
              >
                Posso olhar sua empresa pessoalmente e mostrar quais dessas prioridades eu atacaria primeiro no seu negócio.
              </Typography>

              <Button
                fullWidth
                variant="contained"
                disableElevation
                onClick={() => {
                  window.open(
                    `https://wa.me/5511970349654?text=${whatsappText}`,
                    "_blank"
                  );
                }}
                style={{
                  minHeight: 55,
                  borderRadius: 13,
                  textTransform:
                    "none",
                  fontSize: 13,
                  fontWeight: 900,
                  color: "#FFFFFF",
                  background:
                    "linear-gradient(135deg,#155EEF,#0EA5E9)",
                  boxShadow:
                    "0 12px 25px rgba(37,99,235,.20)"
                }}
              >
                Quero uma análise com o Diego →
              </Button>

              <Typography
                align="center"
                style={{
                  color:
                    "#94A3B8",
                  fontSize: 8.5,
                  marginTop: 9
                }}
              >
                Conversa rápida • sem compromisso
              </Typography>
            </Box>

            <Typography
              align="center"
              style={{
                marginTop: 22,
                color:
                  "#CBD5E1",
                fontSize: 8.5,
                fontWeight: 800,
                letterSpacing:
                  ".04em"
              }}
            >
              DIEGO METAFORMA • ESTRUTURAÇÃO DIGITAL PARA NEGÓCIOS
            </Typography>
          </Box>
        );
      }

      default:
        return null;
    }
  };

  const darkExperience =
    current === "start" ||
    current === "processing" ||
    Boolean(pillarTransition);

  return (
    <Box
      style={{
        minHeight: "100vh",
        background: darkExperience
          ? "radial-gradient(circle at 80% 5%, rgba(37,99,235,.18), transparent 32%), linear-gradient(145deg,#030A14 0%,#071426 50%,#0A1D3C 100%)"
          : "linear-gradient(180deg,#F3F7FC 0%,#F8FAFC 42%,#F4F7FB 100%)",
        padding: "22px 14px 34px",
        boxSizing: "border-box",
        transition: "background .35s ease"
      }}
    >
      <Box
        mx="auto"
        style={{
          width: "100%",
          maxWidth: darkExperience
            ? 680
            : 650
        }}
      >
        {renderHeader()}

        <Box
          style={{
            position: "relative",
            overflow: "hidden",
            background: darkExperience
              ? "linear-gradient(145deg,rgba(7,20,38,.96),rgba(10,29,60,.96))"
              : "#FFFFFF",
            borderRadius: 24,
            padding: darkExperience
              ? "36px 24px"
              : "30px 24px",
            boxShadow: darkExperience
              ? "0 24px 70px rgba(0,0,0,.26)"
              : "0 14px 42px rgba(15,23,42,.065)",
            border: darkExperience
              ? "1px solid rgba(96,165,250,.14)"
              : "1px solid #E5EAF1",
            transition:
              "background .3s ease, border .3s ease, box-shadow .3s ease"
          }}
        >
          {pillarTransition
            ? renderPillarTransition()
            : renderBody()}
        </Box>

        {step > 1 && step < 14 && (
          <Button
            onClick={back}
            style={{
              marginTop: 11,
              paddingLeft: 4,
              textTransform: "none",
              color: "#64748B",
              fontSize: 11,
              fontWeight: 800
            }}
          >
            ← Voltar
          </Button>
        )}
      </Box>
    </Box>
  );
};

const MainNextButton = ({ disabled, onClick }) => (
  <Button
    fullWidth
    variant="contained"
    disableElevation
    disabled={disabled}
    onClick={onClick}
    style={{
      minHeight: 54,
      borderRadius: 14,
      marginTop: 18,
      textTransform: "none",
      fontWeight: 700,
      fontSize: 16,
      background: disabled ? "#d0d5dd" : COLORS.primary,
      color: "#fff"
    }}
  >
    Continuar
  </Button>
);

const ScoreRow = ({ label, score, highlight }) => (
  <Box mb={2}>
    <Box
      display="flex"
      justifyContent="space-between"
      mb={0.7}
    >
      <Typography
        style={{
          fontSize: 13,
          fontWeight: highlight ? 800 : 600,
          color: COLORS.text
        }}
      >
        {label}
        {highlight ? " • atenção" : ""}
      </Typography>

      <Typography
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: COLORS.text
        }}
      >
        {score}/100
      </Typography>
    </Box>

    <Box
      style={{
        height: 7,
        width: "100%",
        borderRadius: 999,
        background: "#eaecf0",
        overflow: "hidden"
      }}
    >
      <Box
        style={{
          width: `${score}%`,
          height: "100%",
          borderRadius: 999,
          background: highlight ? "#344054" : "#101828"
        }}
      />
    </Box>

    <Typography
      style={{
        fontSize: 10,
        marginTop: 4,
        color: "#98a2b3"
      }}
    >
      {scoreLabel(score)}
    </Typography>
  </Box>
);

export default DigitalDiagnostic;
